import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { TFunction } from "i18next";
import "../../assets/css/MicAnimation.css";
import {
    startOpenAIRealtimeWebRTC,
    startOpenAIRealtimeWebRTCCollect,
    startOpenAIRealtimeWebRTCOwner,
} from "../../services/voiceRealtimeWebRTC";

export type VoiceRealtimeBarHandle = {
    /** Starts WebRTC session; no-op if already active or connecting. */
    start: () => Promise<void>;
    stop: () => void;
};

/** Détail renvoyé après tentative de persistance (famla_save_interview_answer). */
type VoicePersistDetail = {
    ok?: boolean;
    next_question?: string;
    step?: unknown;
    status_code?: number;
    description?: string;
};

type Props = {
    /** Process interview (JWT) */
    interviewId?: string | undefined;
    accessToken?: string | undefined;
    /** Data collect contributor magic link — pas de JWT */
    collectInvitationToken?: string | undefined;
    /** Data collect owner workspace (JWT) */
    ownerCollectionId?: string | undefined;
    disabled: boolean;
    t: TFunction;
    pendingQuestionText?: string;
    textDraft?: string;
    onVoiceSessionChange?: (active: boolean) => void;
    onPersistSuccess?: () => void | Promise<void>;
};

const VoiceRealtimeBar = forwardRef<VoiceRealtimeBarHandle, Props>(function VoiceRealtimeBar(
    {
        interviewId,
        accessToken,
        collectInvitationToken,
        ownerCollectionId,
        disabled,
        t,
        pendingQuestionText,
        textDraft = "",
        onVoiceSessionChange,
        onPersistSuccess,
    },
    ref
) {
    const [active, setActive] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastSyncHint, setLastSyncHint] = useState<string | null>(null);
    /** Masque « Dites bonjour… » dès qu’il y a échange réel (micro ou réponse vocale de l’IA). */
    const [conversationStarted, setConversationStarted] = useState(false);
    const stopRef = useRef<(() => void) | null>(null);
    const startLockRef = useRef(false);

    const stop = useCallback(() => {
        if (stopRef.current) {
            stopRef.current();
            stopRef.current = null;
        }
        startLockRef.current = false;
        setActive(false);
        setConnecting(false);
        setConversationStarted(false);
    }, []);

    useEffect(() => {
        return () => {
            if (stopRef.current) {
                stopRef.current();
                stopRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (disabled) {
            stop();
            onVoiceSessionChange?.(false);
        }
    }, [disabled, stop, onVoiceSessionChange]);

    const start = useCallback(async () => {
        if (stopRef.current || startLockRef.current) {
            return;
        }
        if (disabled) {
            return;
        }
        const isCollect = Boolean((collectInvitationToken || "").trim());
        const isOwnerCollect = Boolean((ownerCollectionId || "").trim());
        if (!isCollect && !isOwnerCollect && (!interviewId || !accessToken)) {
            setError(
                t("views.processInterview.voiceLoginRequired", {
                    defaultValue: "Sign in to use voice mode.",
                })
            );
            return;
        }
        if (textDraft.trim().length > 0) {
            setError(
                t("views.processInterview.voiceClearDraftFirst", {
                    defaultValue: "Send or clear your text message before starting voice.",
                })
            );
            return;
        }
        setError(null);
        setLastSyncHint(null);
        setConversationStarted(false);
        startLockRef.current = true;
        setConnecting(true);
        try {
            const pending = pendingQuestionText?.trim() || undefined;
            const trimmedCollectToken = (collectInvitationToken || "").trim();
            const onPersistDetail = (detail: VoicePersistDetail) => {
                if (detail?.ok && detail?.next_question) {
                    const q = detail.next_question;
                    const short = q.length > 120 ? `${q.slice(0, 117)}…` : q;
                    setLastSyncHint(
                        t("views.processInterview.voiceSyncedNextQuestion", {
                            defaultValue: "",
                            snippet: short,
                        })
                    );
                } else if (detail && detail.ok === false) {
                    setLastSyncHint(null);
                    const desc = typeof detail.description === "string" ? detail.description : "";
                    if (detail.status_code === 409) {
                        setError(
                            t("views.processInterview.voiceSaveConflict", {
                                defaultValue:
                                    "Save conflict: the pending question changed. Ask the assistant to use context again, or reload the page.{{serverDetail}}",
                                serverDetail: desc ? ` ${desc}` : "",
                            })
                        );
                    } else if (desc) {
                        setError(
                            t("views.processInterview.voiceSaveFailed", {
                                defaultValue: "Could not save from voice: {{detail}}",
                                detail: desc,
                            })
                        );
                    } else {
                        setError(
                            t("views.processInterview.voiceSaveFailedGeneric", {
                                defaultValue: "Could not save your answer from voice.",
                            })
                        );
                    }
                }
            };

            let stopFn: () => void;
            if (trimmedCollectToken) {
                const { stop } = await startOpenAIRealtimeWebRTCCollect({
                    invitationToken: trimmedCollectToken,
                    clientShownPendingQuestion: pending,
                    onError: (err: Error) => setError(err.message),
                    onStarterHintDismiss: () => setConversationStarted(true),
                    onPersistSuccess,
                    onPersistDetail,
                    onVoiceSessionActiveChange: (v: boolean) => {
                        onVoiceSessionChange?.(v);
                    },
                });
                stopFn = stop;
            } else if (isOwnerCollect) {
                const trimmedCollectionId = (ownerCollectionId || "").trim();
                if (!trimmedCollectionId || !accessToken) {
                    setError(
                        t("views.processInterview.voiceLoginRequired", {
                            defaultValue: "Sign in to use voice mode.",
                        })
                    );
                    return;
                }
                const { stop } = await startOpenAIRealtimeWebRTCOwner({
                    collectionId: trimmedCollectionId,
                    accessToken,
                    clientShownPendingQuestion: pending,
                    onError: (err: Error) => setError(err.message),
                    onStarterHintDismiss: () => setConversationStarted(true),
                    onPersistSuccess,
                    onPersistDetail,
                    onVoiceSessionActiveChange: (v: boolean) => {
                        onVoiceSessionChange?.(v);
                    },
                });
                stopFn = stop;
            } else {
                if (!interviewId || !accessToken) {
                    setError(
                        t("views.processInterview.voiceLoginRequired", {
                            defaultValue: "Sign in to use voice mode.",
                        })
                    );
                    return;
                }
                const { stop } = await startOpenAIRealtimeWebRTC({
                    interviewId,
                    accessToken,
                    clientShownPendingQuestion: pending,
                    onError: (err) => setError(err.message),
                    onStarterHintDismiss: () => setConversationStarted(true),
                    onPersistSuccess,
                    onPersistDetail,
                    onVoiceSessionActiveChange: (v) => {
                        onVoiceSessionChange?.(v);
                    },
                });
                stopFn = stop;
            }
            stopRef.current = stopFn;
            setActive(true);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            setError(msg);
        } finally {
            startLockRef.current = false;
            setConnecting(false);
        }
    }, [
        interviewId,
        accessToken,
        collectInvitationToken,
        ownerCollectionId,
        disabled,
        textDraft,
        pendingQuestionText,
        t,
        onPersistSuccess,
        onVoiceSessionChange,
    ]);

    useImperativeHandle(
        ref,
        () => ({
            start,
            stop,
        }),
        [start, stop]
    );

    const labelStart = t("views.processInterview.voiceConversationStart", {
        defaultValue: "Voice conversation",
    });
    const labelStop = t("views.processInterview.voiceConversationStop", {
        defaultValue: "Stop voice",
    });
    const labelBusy = t("views.processInterview.voiceConnecting", {
        defaultValue: "Connecting…",
    });

    const idleLabel = t("views.processInterview.voiceRealtimeLabel", {
        defaultValue: "Speak with the AI interviewer",
    });
    const starterHint = t("views.processInterview.voiceRealtimeHint", {
        defaultValue: 'Say "Hi" to begin your interview.',
    });
    const showStarterHint = active && !conversationStarted;

    return (
        <div className="interview-voice-strip">
            <div className="interview-voice-strip__row">
                <span className="interview-voice-strip__hint">
                    {!active && idleLabel}
                    {showStarterHint && starterHint}
                </span>
                {!active ? (
                    <button
                        type="button"
                        className="interview-voice-btn interview-voice-btn--outline"
                        disabled={
                            disabled ||
                            connecting ||
                            (!(collectInvitationToken || "").trim() &&
                                !(ownerCollectionId || "").trim() &&
                                !interviewId)
                        }
                        onClick={start}
                    >
                        {connecting ? labelBusy : labelStart}
                    </button>
                ) : (
                    <div className="interview-voice-strip__actions">
                        <button
                            type="button"
                            className="interview-voice-btn interview-voice-btn--danger"
                            onClick={stop}
                        >
                            {labelStop}
                        </button>
                        <div
                            className="mic-container recording interview-voice-strip__mic-indicator"
                            style={{ width: 50, height: 50 }}
                            aria-hidden="true"
                        >
                            <span className="circle" />
                            <span className="circle" />
                            <span className="circle" />
                            <i className="bi bi-mic text-gradient" />
                        </div>
                    </div>
                )}
            </div>
            {error && (
                <div className="interview-voice-strip__error mt-1" role="alert">
                    <i className="fa-solid fa-circle-exclamation me-1" aria-hidden="true" />
                    {error}
                </div>
            )}
            {lastSyncHint && (
                <div className="interview-voice-strip__sync mt-1" aria-live="polite">
                    <i className="fa-solid fa-circle-check me-1" aria-hidden="true" />
                    {lastSyncHint}
                </div>
            )}
        </div>
    );
});

export default VoiceRealtimeBar;
